<template>
  <div
    v-if="show"
    class="apoderado-extra-drawer-overlay"
    @click="closeModal"
  >
    <div class="apoderado-extra-drawer" @click.stop>
      <div class="apoderado-extra-drawer-handle"></div>

      <div class="apoderado-extra-drawer-header">
        <div class="apoderado-extra-drawer-header-content">
          <div class="apoderado-extra-drawer-icon-wrapper">
            <i
              :class="
                view === 'list'
                  ? 'bi bi-people-fill'
                  : 'bi bi-person-plus-fill'
              "
            ></i>
          </div>
          <div class="apoderado-extra-drawer-title-section">
            <h5 class="apoderado-extra-drawer-title">
              {{
                view === "form"
                  ? tx("addTitle")
                  : view === "edit"
                    ? tx("editTitle")
                    : tx("listTitle")
              }}
            </h5>
            <p class="apoderado-extra-drawer-subtitle">
              {{ headerSubtitle }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="apoderado-extra-drawer-close"
          @click="closeModal"
          :aria-label="t('groupUsers.close')"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="apoderado-extra-drawer-body">
        <!-- Vista lista -->
        <div v-if="view === 'list'" class="apoderado-extra-list-section">
          <p class="apoderado-extra-concept">
            {{ tx("concept") }}
          </p>

          <div class="apoderado-extra-list-header">
            <div class="apoderado-extra-list-title-wrapper">
              <i class="bi bi-people-fill apoderado-extra-list-icon"></i>
              <h6 class="apoderado-extra-list-title">{{ tx("listTitle") }}</h6>
            </div>
            <span class="apoderado-extra-count-badge">
              {{ associatedGuardians.length }}
            </span>
          </div>

          <div v-if="isLoading" class="apoderado-extra-loading">
            <div class="apoderado-extra-loading-spinner">
              <i class="bi bi-arrow-repeat"></i>
            </div>
            <p class="apoderado-extra-loading-text">{{ tx("loading") }}</p>
          </div>

          <div
            v-else-if="associatedGuardians.length === 0"
            class="apoderado-extra-empty"
          >
            <div class="apoderado-extra-empty-icon">
              <i class="bi bi-people"></i>
            </div>
            <p class="apoderado-extra-empty-text">
              {{ tx("empty") }}
            </p>
            <p class="apoderado-extra-empty-subtext">
              {{ tx("emptyHint") }}
            </p>
          </div>

          <div v-else class="apoderado-extra-list">
            <div
              v-for="guardian in associatedGuardians"
              :key="guardian.email"
              class="apoderado-extra-card"
            >
              <div class="apoderado-extra-card-avatar">
                <i class="bi bi-person-fill"></i>
              </div>
              <div class="apoderado-extra-card-info">
                <div class="apoderado-extra-card-name">
                  {{ guardian.full_name }}
                  <span
                    v-if="isPendingGuardian(guardian)"
                    class="apoderado-extra-pending-badge"
                  >
                    {{ tx("pendingBadge") }}
                  </span>
                </div>
                <div class="apoderado-extra-card-email">
                  <i class="bi bi-envelope"></i>
                  {{ guardian.email }}
                </div>
                <div
                  v-if="isPendingGuardian(guardian)"
                  class="apoderado-extra-card-pending"
                >
                  {{ tx("pendingHint") }}
                </div>
              </div>
              <div class="apoderado-extra-card-actions">
                <button
                  v-if="isPendingGuardian(guardian)"
                  type="button"
                  class="apoderado-extra-resend-btn"
                  :disabled="resendingEmail === guardian.email"
                  :aria-label="tx('resend')"
                  @click="resendGuardianInvite(guardian)"
                >
                  <span
                    v-if="resendingEmail === guardian.email"
                    class="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                  <i v-else class="bi bi-send"></i>
                </button>
                <button
                  type="button"
                  class="apoderado-extra-resend-btn"
                  :aria-label="tx('editAria', { name: guardian.full_name || guardian.email })"
                  @click="openEditGuardian(guardian)"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button
                  type="button"
                  class="apoderado-extra-delete-btn"
                  :disabled="deletingEmail === guardian.email"
                  :aria-label="tx('deleteAria', { name: guardian.full_name || guardian.email })"
                  @click="confirmDeleteGuardian(guardian)"
                >
                  <span
                    v-if="deletingEmail === guardian.email"
                    class="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                  <i v-else class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="apoderado-extra-submit-btn mt-3"
            @click="startCreate"
          >
            <i class="bi bi-plus-circle me-2"></i>
            {{ tx("addTitle") }}
          </button>
        </div>

        <!-- Vista formulario -->
        <div v-else class="apoderado-extra-form-section">
          <button
            v-if="associatedGuardians.length > 0"
            type="button"
            class="apoderado-extra-back"
            @click="backToList"
          >
            <i class="bi bi-arrow-left me-1"></i>
            {{ tx("viewList") }}
          </button>

          <p class="apoderado-extra-concept">
            {{ view === "edit" ? tx("concept") : tx("formConcept") }}
          </p>

          <form @submit.prevent="view === 'edit' ? handleSaveEdit() : handleSubmit()" novalidate>
            <div class="apoderado-extra-form-group">
              <label for="fullName" class="apoderado-extra-label">
                <i class="bi bi-person me-1"></i>
                {{ tx("fullName") }} <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="apoderado-extra-input"
                id="fullName"
                v-model.trim="formData.full_name"
                maxlength="100"
                required
                :placeholder="tx('namePlaceholder')"
              />
            </div>

            <div class="apoderado-extra-form-group">
              <label for="email" class="apoderado-extra-label">
                <i class="bi bi-envelope me-1"></i>
                {{ tx("email") }} <span class="text-danger">*</span>
              </label>
              <input
                type="email"
                class="apoderado-extra-input"
                id="email"
                v-model.trim="formData.email"
                maxlength="100"
                required
                placeholder="ejemplo@correo.com"
                :readonly="view === 'edit'"
                :disabled="view === 'edit'"
              />
              <p v-if="view === 'edit'" class="form-text mb-0">
                {{ tx("emailReadonly") }}
              </p>
            </div>

            <div class="apoderado-extra-form-group">
              <label for="phone" class="apoderado-extra-label">
                <i class="bi bi-telephone me-1"></i>
                {{ tx("phone") }}
                <span class="text-muted small">{{ tx("optional") }}</span>
              </label>
              <input
                type="tel"
                class="apoderado-extra-input"
                id="phone"
                v-model.trim="formData.phone"
                placeholder="+569XXXXXXXX"
                pattern="^\+569\d{8}$"
              />
            </div>

            <NationalIdInput
              v-model="formData.rut"
              :country="nationalIdCountry"
              :label="tx('nationalId')"
              :hint="tx('nationalIdHint')"
              optional
              :optional-label="tx('optional')"
              input-class="apoderado-extra-input"
              input-id="extra-national-id"
            />

            <ExtraGuardianCertFields
              :for-email="formData.email"
              :school-id="formData.school_id"
              :year="formData.year"
              :course-code="formData.course_code"
              :title="tx('documentsTitle')"
              :hint="tx('documentsHint')"
              :need-email="tx('documentsNeedEmail')"
              :inhabilidades-label="tx('inhabilidades')"
              :antecedentes-label="tx('antecedentes')"
              :initial-documents="editingDocuments"
            />

            <button
              v-if="view === 'edit' && !isPendingGuardian(editingGuardian)"
              type="button"
              class="apoderado-extra-qr-btn"
              :disabled="sendingQr"
              @click="sendAccessQr"
            >
              <span
                v-if="sendingQr"
                class="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
              <i v-else class="bi bi-qr-code me-2"></i>
              {{ tx("sendQr") }}
            </button>
            <p
              v-else-if="view === 'edit'"
              class="form-text"
            >
              {{ tx("sendQrPending") }}
            </p>

            <button
              type="submit"
              class="apoderado-extra-submit-btn"
              :disabled="isSubmitting"
            >
              <i class="bi bi-check-circle me-2"></i>
              <span v-if="isSubmitting">
                {{ view === "edit" ? tx("savingEdit") : tx("associating") }}
              </span>
              <span v-else>
                {{ view === "edit" ? tx("saveEdit") : tx("link") }}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showSuccessModal"
    class="apoderado-extra-success-overlay"
    @click="closeSuccessModal"
  >
    <div class="apoderado-extra-success-card" @click.stop>
      <div class="apoderado-extra-success-icon">
        <i class="bi bi-check-circle-fill"></i>
      </div>
      <h6 class="apoderado-extra-success-title">{{ tx("success") }}</h6>
      <p class="apoderado-extra-success-text">
        {{ tx("successDetail", { email: invitedEmail }) }}
      </p>
      <button class="apoderado-extra-success-btn" @click="closeSuccessModal">
        {{ tx("accept") }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { useCursoStore } from "@/store/cursoStore";
import { useI18n } from "vue-i18n";
import { courseRequiresStudentName } from "@/utils/parseCourseCode";
import { alerta, confirmar } from "@/composables/confirmacionApp";
import { useGuardianContext } from "@/composables/useGuardianContext";
import NationalIdInput from "@/components/NationalIdInput.vue";
import ExtraGuardianCertFields from "@/components/ExtraGuardianCertFields.vue";
import { apoderadosService } from "@/services/apoderadosService";
import { invalidNationalIdMessage, isValidNationalId } from "@/utils/nationalId";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  masterUser: {
    type: String,
    default: "",
  },
  contextStudendUserCourseSeq: {
    type: String,
    required: false,
  },
  /** Nombre del alumno de la fila (tesorero). No usar el pupilo del store. */
  contextStudentName: {
    type: String,
    default: "",
  },
  /** 'list' = panel de asociados; 'form' = agregar */
  startView: {
    type: String,
    default: "list",
    validator: (v) => v === "list" || v === "form",
  },
});

const emit = defineEmits(["close", "success", "changed"]);

const router = useRouter();
const cursoStore = useCursoStore();
const { t } = useI18n();
const { isAdditionalGuardian } = useGuardianContext();

const isSelfManagementBlocked = () =>
  isAdditionalGuardian.value && !String(props.masterUser || "").trim();

const view = ref("list");
const showSuccessModal = ref(false);
const associatedGuardians = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const deletingEmail = ref("");
const resendingEmail = ref("");
const invitedEmail = ref("");
const editingGuardian = ref(null);
const sendingQr = ref(false);

const labelNs = computed(() =>
  courseRequiresStudentName(cursoStore.selectedCursoData)
    ? "groupUsers.extraGuardian"
    : "groupUsers.extraUser",
);

const tx = (key, params) => t(`${labelNs.value}.${key}`, params ?? {});

const formData = ref({
  full_name: "",
  email: "",
  phone: "",
  rut: "",
  course_code: "",
  school_id: "",
  year: "",
  student_userCourse_seq: "",
  master_user: "",
});

const courseInfo = ref({
  course_name: "",
  student_name: "",
});

const nationalIdCountry = computed(
  () => cursoStore.selectedCursoData?.country_code || "CL",
);

const editingDocuments = computed(
  () => editingGuardian.value?.documents || null,
);

const emptyFormFields = () => ({
  full_name: "",
  email: "",
  phone: "",
  rut: "",
  course_code: formData.value.course_code,
  school_id: formData.value.school_id,
  year: formData.value.year,
  student_userCourse_seq: formData.value.student_userCourse_seq,
  master_user: formData.value.master_user,
});

const backToList = () => {
  editingGuardian.value = null;
  view.value = "list";
  formData.value = emptyFormFields();
};

const startCreate = () => {
  editingGuardian.value = null;
  formData.value = emptyFormFields();
  view.value = "form";
};

const openEditGuardian = (guardian) => {
  editingGuardian.value = guardian;
  formData.value = {
    ...formData.value,
    full_name: guardian.full_name || "",
    email: guardian.email || "",
    phone: guardian.phone || "",
    rut: guardian.rut || "",
  };
  view.value = "edit";
};

const headerSubtitle = computed(() => {
  const student = String(courseInfo.value.student_name || "").trim();
  const course = String(courseInfo.value.course_name || "").trim();
  if (student) return tx("subtitle", { name: student });
  if (course) return course;
  return tx("loading");
});

const normalizeStudendSeq = (v) =>
  v == null || String(v).trim() === "" ? "" : String(v).trim();

const isTesoreroGestion = () =>
  props.masterUser != null && String(props.masterUser).trim() !== "";

const resolveStudentUserCourseSeq = (cursoData) => {
  if (isTesoreroGestion()) {
    return normalizeStudendSeq(props.contextStudendUserCourseSeq ?? "");
  }
  return normalizeStudendSeq(
    cursoData?.studend_userCourse_seq ?? cursoData?.student_userCourse_seq
  );
};

const resolveStudentName = (cursoData) => {
  if (isTesoreroGestion()) {
    return String(props.contextStudentName || "").trim();
  }
  return String(cursoData?.student_name || "").trim();
};

const closeModal = () => {
  emit("close");
  formData.value = emptyFormFields();
};

const closeSuccessModal = () => {
  showSuccessModal.value = false;
  view.value = "list";
  emit("success");
  emit("changed");
};

const getCreateExtraGuardianErrorMessage = (error) => {
  const fallback = tx("saveError");
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if (typeof data.error === "string" && data.error.trim()) {
    return data.error.trim();
  }
  return fallback;
};

const getDeleteErrorMessage = (error) => {
  const fallback = tx("deleteError");
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if (typeof data.error === "string" && data.error.trim()) {
    return data.error.trim();
  }
  return fallback;
};

const isPendingGuardian = (guardian) =>
  String(guardian?.invite_status || "").toUpperCase() === "PENDING";

const getResendErrorMessage = (error) => {
  const fallback = tx("resendError");
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  return fallback;
};

const resendGuardianInvite = async (guardian) => {
  const { school_id, year, course_code, student_userCourse_seq, master_user } =
    formData.value;
  if (!guardian?.email || !school_id || !year || !course_code) {
    alert(tx("resendError"));
    return;
  }

  resendingEmail.value = guardian.email;
  try {
    const authToken = localStorage.getItem("authToken");
    await axios.post(
      "https://front.microcentrum.cl/v1/apoderados?op=resendExtraGuardianInvite",
      {
        email: guardian.email,
        school_id,
        year,
        course_code,
        full_name: guardian.full_name,
        student_userCourse_seq: student_userCourse_seq || undefined,
        master_user: master_user || undefined,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: authToken,
        },
      },
    );
    alert(tx("resendOk"));
  } catch (error) {
    console.error("Error al reenviar invitación:", error);
    alert(getResendErrorMessage(error));
  } finally {
    resendingEmail.value = "";
  }
};

const confirmDeleteGuardian = async (guardian) => {
  const name = String(guardian?.full_name || guardian?.email || "").trim();
  if (!name || !guardian?.email) return;

  const confirmed = await confirmar({
    titulo: tx("deleteConfirmTitle"),
    mensaje: tx("deleteConfirmQuestion", { name }),
    detalle: isPendingGuardian(guardian)
      ? tx("deletePendingDetail")
      : tx("deleteConfirmDetail"),
    textoConfirmar: tx("deleteConfirm"),
    textoCancelar: t("common.cancel"),
    variante: "danger",
    icono: "bi-trash",
  });

  if (!confirmed) return;
  await deleteGuardian(guardian);
};

const deleteGuardian = async (guardian) => {
  const { school_id, year, course_code, student_userCourse_seq } =
    formData.value;

  if (!guardian?.email || !school_id || !year || !course_code) {
    alert(tx("deleteError"));
    return;
  }

  deletingEmail.value = guardian.email;

  try {
    const authToken = localStorage.getItem("authToken");
    await axios.post(
      "https://front.microcentrum.cl/v1/apoderados?op=deleteExtraGuardian",
      {
        email: guardian.email,
        school_id,
        year,
        course_code,
        student_userCourse_seq: student_userCourse_seq || undefined,
        master_user: formData.value.master_user || undefined,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: authToken,
        },
      },
    );

    await loadAssociatedGuardians();
    emit("changed");
  } catch (error) {
    console.error("Error al eliminar usuario adicional:", error);
    alert(getDeleteErrorMessage(error));
  } finally {
    deletingEmail.value = "";
  }
};

const loadAssociatedGuardians = async () => {
  if (isSelfManagementBlocked()) {
    isLoading.value = false;
    return;
  }
  isLoading.value = true;

  const cursoData = cursoStore.selectedCursoData;
  if (!cursoData) {
    isLoading.value = false;
    return;
  }

  const school_id = cursoData.school_id;
  const year = cursoData.year;
  const course_code = cursoData.course_id;
  const student_userCourse_seq = resolveStudentUserCourseSeq(cursoData);
  const student_name = resolveStudentName(cursoData);

  courseInfo.value = {
    course_name: cursoData.course_name || cursoData.course_id,
    student_name,
  };

  formData.value = {
    ...formData.value,
    course_code,
    year,
    school_id,
    student_userCourse_seq,
    master_user: props.masterUser,
  };

  try {
    let url = `https://front.microcentrum.cl/v1/apoderados?op=getExtrasByCourseGuardian&school_id=${school_id}&year=${year}&code=${course_code}`;

    if (student_userCourse_seq) {
      url += `&studend_userCourse_seq=${encodeURIComponent(
        student_userCourse_seq
      )}`;
    }

    if (formData.value.master_user) {
      url += `&master_user=${encodeURIComponent(formData.value.master_user)}`;
    }

    const authToken = localStorage.getItem("authToken");
    const response = await axios.get(url, {
      headers: {
        Authorization: authToken,
      },
    });
    associatedGuardians.value = response.data.guardians || [];
  } catch (error) {
    console.error("Error al cargar los usuarios asociados:", error);
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = async () => {
  try {
    const { full_name, email } = formData.value;

    if (!full_name || !email) {
      alert(tx("required"));
      return;
    }

    if (isTesoreroGestion() && !String(courseInfo.value.student_name || "").trim()) {
      alert(tx("missingStudent"));
      return;
    }

    if (formData.value.rut && !isValidNationalId(nationalIdCountry.value, formData.value.rut)) {
      alert(invalidNationalIdMessage(nationalIdCountry.value));
      return;
    }

    isSubmitting.value = true;
    const authToken = localStorage.getItem("authToken");

    const response = await axios.post(
      "https://front.microcentrum.cl/v1/apoderados?op=createExtraGuardian",
      {
        full_name: formData.value.full_name,
        email: formData.value.email,
        phone: formData.value.phone,
        rut: formData.value.rut || undefined,
        country_code: nationalIdCountry.value,
        course_id: formData.value.course_code,
        course_code: formData.value.course_code,
        school_id: formData.value.school_id,
        year: formData.value.year,
        student_name: courseInfo.value.student_name || undefined,
        student_userCourse_seq: formData.value.student_userCourse_seq,
        master_user: formData.value.master_user,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: authToken,
        },
      }
    );

    if (response.status === 200) {
      invitedEmail.value = String(formData.value.email || "").trim();
      showSuccessModal.value = true;

      const {
        course_code,
        school_id,
        year,
        student_userCourse_seq,
        master_user,
      } = formData.value;
      formData.value = {
        full_name: "",
        email: "",
        phone: "",
        rut: "",
        course_code,
        school_id,
        year,
        student_userCourse_seq,
        master_user,
      };

      await loadAssociatedGuardians();
      emit("changed");
    }
  } catch (error) {
    console.error("❌ Error al guardar usuario:", error);
    alert(getCreateExtraGuardianErrorMessage(error));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSaveEdit = async () => {
  const { full_name, email, school_id, year, course_code, phone, rut } =
    formData.value;
  if (!full_name || !email || !school_id || !year || !course_code) {
    alert(tx("required"));
    return;
  }
  if (rut && !isValidNationalId(nationalIdCountry.value, rut)) {
    alert(invalidNationalIdMessage(nationalIdCountry.value));
    return;
  }
  isSubmitting.value = true;
  try {
    await apoderadosService.updateExtraGuardian({
      email,
      school_id,
      year,
      course_code,
      full_name,
      phone,
      rut: rut || "",
      country_code: nationalIdCountry.value,
    });
    associatedGuardians.value = associatedGuardians.value.map((guardian) =>
      String(guardian.email || "").toLowerCase() === email.toLowerCase()
        ? {
            ...guardian,
            full_name,
            phone,
            rut: rut || guardian.rut,
          }
        : guardian
    );
    await loadAssociatedGuardians();
    emit("changed");
    backToList();
  } catch (error) {
    console.error("Error al editar adicional:", error);
    alert(getCreateExtraGuardianErrorMessage(error));
  } finally {
    isSubmitting.value = false;
  }
};

const sendAccessQr = async () => {
  const { email, school_id, year, course_code } = formData.value;
  if (!email || !school_id || !year || !course_code) {
    await alerta({
      titulo: tx("sendQrErrorTitle"),
      mensaje: tx("sendQrError"),
      textoConfirmar: t("common.understood"),
      variante: "danger",
      icono: "bi-x-circle",
    });
    return;
  }

  const confirmed = await confirmar({
    titulo: tx("sendQrConfirmTitle"),
    mensaje: tx("sendQrConfirm", { email }),
    detalle: tx("sendQrConfirmDetail"),
    textoConfirmar: tx("sendQrConfirmAction"),
    textoCancelar: t("common.cancel"),
    variante: "primary",
    icono: "bi-qr-code",
  });
  if (!confirmed) return;

  sendingQr.value = true;
  try {
    await apoderadosService.sendExtraGuardianAccessEmail({
      email,
      school_id,
      year,
      course_code,
    });
    await alerta({
      titulo: tx("sendQrOkTitle"),
      mensaje: tx("sendQrOk", { email }),
      textoConfirmar: t("common.understood"),
      variante: "success",
      icono: "bi-check-circle",
    });
  } catch (error) {
    console.error("Error al enviar QR:", error);
    const data = error?.response?.data;
    await alerta({
      titulo: tx("sendQrErrorTitle"),
      mensaje:
        (typeof data?.message === "string" && data.message) || tx("sendQrError"),
      textoConfirmar: t("common.understood"),
      variante: "danger",
      icono: "bi-x-circle",
    });
  } finally {
    sendingQr.value = false;
  }
};

watch(
  () => props.show,
  (newValue) => {
    if (!newValue) return;
    if (isSelfManagementBlocked()) {
      emit("close");
      if (router.currentRoute.value.name !== "me-hub") {
        router.replace({ name: "me-hub" });
      }
      return;
    }
    view.value = props.startView === "form" ? "form" : "list";
    associatedGuardians.value = [];
    editingGuardian.value = null;
    formData.value = {
      full_name: "",
      email: "",
      phone: "",
      rut: "",
      course_code: "",
      school_id: "",
      year: "",
      student_userCourse_seq: "",
      master_user: "",
    };
    loadAssociatedGuardians();
  }
);

watch(
  () => props.startView,
  (v) => {
    if (props.show) {
      view.value = v === "form" ? "form" : "list";
    }
  }
);
</script>

<style scoped>
.apoderado-extra-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.apoderado-extra-drawer {
  background: var(--mc-bg-card);
  color: var(--mc-text-primary);
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.25s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(40px);
    opacity: 0.6;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.apoderado-extra-drawer-handle {
  width: 40px;
  height: 4px;
  background: var(--mc-border-secondary);
  border-radius: 999px;
  margin: 10px auto 0;
}

.apoderado-extra-drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem 0.5rem;
  border-bottom: 1px solid var(--mc-border-primary);
}

.apoderado-extra-drawer-header-content {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  min-width: 0;
}

.apoderado-extra-drawer-icon-wrapper {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  background: var(--mc-primary-soft, rgba(30, 58, 95, 0.12));
  color: var(--mc-primary, #1e3a5f);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.2rem;
}

.apoderado-extra-drawer-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--mc-text-primary);
}

.apoderado-extra-drawer-subtitle {
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: var(--mc-text-secondary);
}

.apoderado-extra-drawer-close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--mc-bg-tertiary, transparent);
  color: var(--mc-text-secondary);
  font-size: 1.1rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.apoderado-extra-drawer-body {
  padding: 1rem;
  overflow-y: auto;
}

.apoderado-extra-concept {
  font-size: 0.85rem;
  color: var(--mc-text-secondary);
  line-height: 1.4;
  margin: 0 0 1rem;
}

.apoderado-extra-back {
  border: none;
  background: transparent;
  color: var(--mc-primary, #1e3a5f);
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0;
  margin-bottom: 0.75rem;
}

.apoderado-extra-form-group {
  margin-bottom: 0.9rem;
}

.apoderado-extra-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--mc-text-primary);
  margin-bottom: 0.35rem;
}

.apoderado-extra-input {
  width: 100%;
  border: 1px solid var(--mc-border-secondary);
  border-radius: 10px;
  padding: 0.7rem 0.85rem;
  font-size: 0.95rem;
  background: var(--mc-bg-primary, #fff);
  color: var(--mc-text-primary);
}

.apoderado-extra-input::placeholder {
  color: var(--mc-text-muted, #6c757d);
}

.apoderado-extra-input:focus {
  outline: none;
  border-color: var(--mc-border-focus, #1e3a5f);
  box-shadow: 0 0 0 3px var(--mc-primary-soft, rgba(30, 58, 95, 0.15));
}

.apoderado-extra-submit-btn {
  width: 100%;
  border: none;
  border-radius: 12px;
  background: var(--mc-brand-solid, #1e3a5f);
  color: #fff;
  font-weight: 700;
  padding: 0.85rem 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.apoderado-extra-submit-btn:disabled {
  opacity: 0.65;
}

.apoderado-extra-qr-btn {
  width: 100%;
  margin: 0.75rem 0 0.5rem;
  border: 1px solid var(--mc-primary, #1e3a5f);
  background: transparent;
  color: var(--mc-primary, #1e3a5f);
  font-weight: 700;
  border-radius: 12px;
  padding: 0.7rem 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.apoderado-extra-qr-btn:disabled {
  opacity: 0.65;
}

.apoderado-extra-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.apoderado-extra-list-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.apoderado-extra-list-icon {
  color: var(--mc-primary, #1e3a5f);
}

.apoderado-extra-list-title {
  margin: 0;
  font-weight: 700;
  color: var(--mc-text-primary);
}

.apoderado-extra-count-badge {
  background: var(--mc-primary-soft, #e0f2fe);
  color: var(--mc-text-primary);
  font-weight: 700;
  font-size: 0.8rem;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
}

.apoderado-extra-loading,
.apoderado-extra-empty {
  text-align: center;
  padding: 1.25rem 0.5rem;
  color: var(--mc-text-secondary);
}

.apoderado-extra-loading-spinner i {
  font-size: 1.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.apoderado-extra-empty-icon {
  font-size: 2rem;
  color: var(--mc-text-muted, #94a3b8);
  margin-bottom: 0.5rem;
}

.apoderado-extra-empty-text {
  margin: 0;
  font-weight: 600;
  color: var(--mc-text-primary);
}

.apoderado-extra-empty-subtext {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
}

.apoderado-extra-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.apoderado-extra-card {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  border: 1px solid var(--mc-border-primary);
  border-radius: 12px;
  padding: 0.7rem 0.8rem;
  background: var(--mc-bg-secondary);
}

.apoderado-extra-card-avatar {
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 999px;
  background: var(--mc-primary-soft, rgba(30, 58, 95, 0.12));
  color: var(--mc-primary, #1e3a5f);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.apoderado-extra-card-info {
  min-width: 0;
  flex: 1;
}

.apoderado-extra-card-name {
  font-weight: 700;
  color: var(--mc-text-primary);
  font-size: 0.92rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.apoderado-extra-pending-badge {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #92400e;
  background: #fef3c7;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
}

.apoderado-extra-card-pending {
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: #92400e;
}

.apoderado-extra-card-email {
  font-size: 0.78rem;
  color: var(--mc-text-secondary);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.apoderado-extra-card-actions {
  flex-shrink: 0;
  display: flex;
  gap: 0.3rem;
}

.apoderado-extra-resend-btn {
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--mc-border-secondary);
  border-radius: 10px;
  background: transparent;
  color: var(--mc-primary, #1e3a5f);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.apoderado-extra-delete-btn {
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--mc-border-secondary);
  border-radius: 10px;
  background: transparent;
  color: var(--mc-danger, #dc2626);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.apoderado-extra-delete-btn:disabled {
  opacity: 0.6;
  cursor: wait;
}

.apoderado-extra-delete-btn:not(:disabled):active {
  transform: scale(0.96);
}

.apoderado-extra-success-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.apoderado-extra-success-card {
  background: var(--mc-bg-card);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 340px;
  width: 100%;
  text-align: center;
}

.apoderado-extra-success-icon {
  font-size: 2.5rem;
  color: var(--mc-success, #16a34a);
  margin-bottom: 0.5rem;
}

.apoderado-extra-success-title {
  font-weight: 700;
  margin-bottom: 0.35rem;
  color: var(--mc-text-primary);
}

.apoderado-extra-success-text {
  color: var(--mc-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.apoderado-extra-success-btn {
  border: none;
  border-radius: 10px;
  background: var(--mc-brand-solid, #1e3a5f);
  color: #fff;
  font-weight: 700;
  padding: 0.65rem 1.25rem;
  width: 100%;
}

</style>
